import { SETUP_STATUS_LABELS, type SetupStepStatus } from '@lia/contracts';
import { Alert02Icon, CheckmarkCircle02Icon, CircleIcon, NextIcon, RecordIcon } from '@hugeicons/core-free-icons';
import { Icon } from '../primitives/icon.js';
import * as React from 'react';
import { cn } from '../../lib/cn.js';
import { StatusBadge } from '../primitives/badge.js';
import { ProgressBar } from '../primitives/progress.js';
import { Skeleton } from '../primitives/states.js';
import { SETUP_STATUS_TONE } from './status.js';

export interface SetupStep {
  key: string;
  title: string;
  description?: string;
  status: SetupStepStatus;
  /** Explanation when status is needs_attention. */
  attentionMessage?: string;
  /** Primary action for the step, e.g. a Button or link ("Continuar", "Revisar"). */
  action?: React.ReactNode;
}

export interface SetupChecklistProps {
  steps: readonly SetupStep[];
  title?: string;
  loading?: boolean;
  className?: string;
}

const STEP_ICON: Record<SetupStepStatus, React.ReactNode> = {
  pending: <Icon icon={CircleIcon} aria-hidden className="text-text-secondary" />,
  in_progress: <Icon icon={RecordIcon} aria-hidden className="text-info" />,
  ready: <Icon icon={CheckmarkCircle02Icon} aria-hidden className="text-success" />,
  skipped: <Icon icon={NextIcon} aria-hidden className="text-text-secondary" />,
  needs_attention: <Icon icon={Alert02Icon} aria-hidden className="text-warning" />,
};

/** Ordered, resumable onboarding checklist with progress (ready + skipped count as done). */
export const SetupChecklist = React.forwardRef<HTMLDivElement, SetupChecklistProps>(function SetupChecklist(
  { steps, title = 'Configuración del espacio', loading, className },
  ref,
) {
  const done = steps.filter((s) => s.status === 'ready' || s.status === 'skipped').length;
  const pct = steps.length ? (done / steps.length) * 100 : 0;
  const titleId = React.useId();
  return (
    <section ref={ref} aria-labelledby={titleId} className={cn('grid gap-4', className)}>
      <div className="grid gap-2">
        <h3 id={titleId} className="text-body-lg font-semibold text-text-primary">
          {title}
        </h3>
        {loading ? (
          <Skeleton className="h-2 w-full" />
        ) : (
          <ProgressBar value={pct} label="Progreso de configuración" valueText={`${done} de ${steps.length} pasos completos`} />
        )}
      </div>
      {loading ? (
        <div role="status" aria-label="Cargando pasos" className="grid gap-3">
          {Array.from({ length: 4 }, (_, i) => (
            <Skeleton key={i} className="h-14 w-full" />
          ))}
        </div>
      ) : (
        <ol className="grid gap-0 divide-y divide-border-subtle rounded-card border border-border-subtle bg-surface-default">
          {steps.map((s, i) => (
            <li key={s.key} className={cn('flex flex-wrap items-start gap-3 px-4 py-3', s.status === 'in_progress' && 'bg-surface-subtle')}>
              <span className="mt-0.5 [&_svg]:size-5">{STEP_ICON[s.status]}</span>
              <div className="grid min-w-0 flex-1 gap-0.5">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={cn('text-body font-medium text-text-primary', s.status === 'skipped' && 'text-text-secondary')}>
                    <span className="ef-tabular text-text-secondary">{i + 1}. </span>
                    {s.title}
                  </span>
                  <StatusBadge tone={SETUP_STATUS_TONE[s.status]} icon={false}>
                    {SETUP_STATUS_LABELS[s.status]}
                  </StatusBadge>
                </div>
                {s.description ? <p className="text-meta text-text-secondary">{s.description}</p> : null}
                {s.status === 'needs_attention' && s.attentionMessage ? (
                  <p className="text-meta font-medium text-warning">{s.attentionMessage}</p>
                ) : null}
              </div>
              {s.action ? <div className="shrink-0">{s.action}</div> : null}
            </li>
          ))}
        </ol>
      )}
    </section>
  );
});
